import { useState, useEffect } from 'react';
import { Plus, Briefcase, Trash2, Edit2, Loader2 } from 'lucide-react';
import { getExperiences, createExperience, updateExperience, deleteExperience } from '../../services/experiencesApi';
import type { Experience } from '../../services/experiencesApi';

export function AdminExperiences() {
    const [experiences, setExperiences] = useState<Experience[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        company: '',
        role: '',
        period: '',
        duration: '',
        location: '',
        description: '',
        achievements: '' as string, // we will split by newline
        skills: '' as string, // we will split by comma
        current: false
    });

    useEffect(() => {
        fetchExperiences();
    }, []);

    const fetchExperiences = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getExperiences();
            setExperiences(data || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur réseau');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenForm = (exp?: Experience) => {
        if (exp) {
            setShowForm(true);
            setEditingId(exp.id);
            setFormData({
                company: exp.company,
                role: exp.role,
                period: exp.period,
                duration: exp.duration,
                location: exp.location,
                description: exp.description,
                achievements: (exp.achievements || []).join('\n'),
                skills: (exp.skills || []).join(', '),
                current: exp.current || false
            });
        } else {
            setShowForm(true);
            setEditingId(null);
            setFormData({
                company: '',
                role: '',
                period: '',
                duration: '',
                location: '',
                description: '',
                achievements: '',
                skills: '',
                current: false
            });
        }
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditingId(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);
            setError(null);

            const expData = {
                ...formData,
                achievements: formData.achievements.split('\n').map(s => s.trim()).filter(Boolean),
                skills: formData.skills.split(',').map(s => s.trim()).filter(Boolean),
            };

            if (editingId) {
                await updateExperience(editingId, expData);
            } else {
                await createExperience(expData);
            }

            await fetchExperiences();
            setShowForm(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Une erreur est survenue');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Voulez-vous vraiment supprimer cette expérience ?')) return;

        try {
            setLoading(true);
            await deleteExperience(id);
            await fetchExperiences();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
            setLoading(false);
        }
    };

    if (loading && experiences.length === 0) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-[#1f75cb]" />
            </div>
        );
    }

    return (
        <div className="space-y-6 flex flex-col items-center max-w-5xl mx-auto">
            <div className="flex justify-between items-center w-full">
                <h1 className="text-2xl font-bold text-[#1d1d1f] flex items-center gap-2">
                    <Briefcase className="w-8 h-8 text-[#1f75cb]" />
                    Expériences
                </h1>
                <button
                    onClick={() => handleOpenForm()}
                    className="flex items-center gap-2 px-4 py-2 bg-[#1f75cb] text-white rounded-lg hover:bg-[#1a62a8] transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Ajouter
                </button>
            </div>

            {error && (
                <div className="bg-red-50 text-red-600 p-4 rounded-lg border border-red-100 w-full">
                    {error}
                </div>
            )}

            {showForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-bold mb-6">
                            {editingId ? 'Modifier l\'expérience' : 'Nouvelle expérience'}
                        </h2>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Entreprise *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.company}
                                        onChange={e => setFormData({ ...formData, company: e.target.value })}
                                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1f75cb]/20"
                                        placeholder="Ex: Capgemini"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Rôle *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.role}
                                        onChange={e => setFormData({ ...formData, role: e.target.value })}
                                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1f75cb]/20"
                                        placeholder="Ex: Ingénieur DevOps"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Période *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.period}
                                        onChange={e => setFormData({ ...formData, period: e.target.value })}
                                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1f75cb]/20"
                                        placeholder="Ex: 2022 - 2024"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Durée *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.duration}
                                        onChange={e => setFormData({ ...formData, duration: e.target.value })}
                                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1f75cb]/20"
                                        placeholder="Ex: 2 ans"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Lieu *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.location}
                                        onChange={e => setFormData({ ...formData, location: e.target.value })}
                                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1f75cb]/20"
                                        placeholder="Ex: Bordeaux"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                                <textarea
                                    required
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1f75cb]/20 min-h-[100px]"
                                    placeholder="Description des missions..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Réalisations (1 par ligne)</label>
                                <textarea
                                    value={formData.achievements}
                                    onChange={e => setFormData({ ...formData, achievements: e.target.value })}
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1f75cb]/20 min-h-[80px]"
                                    placeholder="- Conception de solution...&#10;- Développement d'API..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Compétences / Techs (séparées par des virgules)</label>
                                <input
                                    type="text"
                                    value={formData.skills}
                                    onChange={e => setFormData({ ...formData, skills: e.target.value })}
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1f75cb]/20"
                                    placeholder="Python, Kubernetes, Docker..."
                                />
                            </div>

                            <div className="flex items-center gap-2 mt-4">
                                <input
                                    type="checkbox"
                                    id="current"
                                    checked={formData.current}
                                    onChange={e => setFormData({ ...formData, current: e.target.checked })}
                                    className="w-4 h-4 text-[#1f75cb] border-gray-300 rounded focus:ring-[#1f75cb]"
                                />
                                <label htmlFor="current" className="text-sm font-medium text-gray-700">En cours (poste actuel)</label>
                            </div>

                            <div className="flex justify-end gap-3 mt-8">
                                <button
                                    type="button"
                                    onClick={handleCloseForm}
                                    className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-4 py-2 bg-[#1f75cb] text-white rounded-lg hover:bg-[#1a62a8] transition-colors flex items-center justify-center font-medium disabled:opacity-50"
                                >
                                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Enregistrer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {experiences.length === 0 && !loading && !error ? (
                <div className="text-center py-12 bg-white rounded-xl border border-gray-100 w-full">
                    <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Aucune expérience n'est configurée pour le moment.</p>
                </div>
            ) : (
                <div className="w-full space-y-4">
                    {experiences.map(exp => (
                        <div key={exp.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 relative group flex flex-col md:flex-row gap-6">
                            <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={() => handleOpenForm(exp)}
                                    className="p-1.5 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100"
                                >
                                    <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => handleDelete(exp.id)}
                                    className="p-1.5 bg-red-50 text-red-600 rounded-md hover:bg-red-100"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="md:w-1/4 shrink-0">
                                <h3 className="font-bold text-gray-900 leading-tight mb-1 pr-16">{exp.company}</h3>
                                <p className="text-sm font-medium text-[#1f75cb] mb-1">{exp.role}</p>
                                <p className="text-xs text-gray-500">{exp.period}</p>
                                <p className="text-xs text-gray-400">{exp.duration} • {exp.location}</p>
                                {exp.current && (
                                    <span className="mt-2 inline-block px-2 py-1 bg-green-50 text-green-600 text-[10px] font-bold uppercase rounded-full">Actuel</span>
                                )}
                            </div>

                            <div className="md:w-3/4">
                                <p className="text-sm text-gray-600 whitespace-pre-line mb-3">{exp.description}</p>
                                
                                {exp.achievements && exp.achievements.length > 0 && (
                                    <div className="mb-3">
                                        <h4 className="text-xs font-bold text-gray-700 mb-1">Réalisations</h4>
                                        <ul className="list-disc pl-4 text-sm text-gray-600 space-y-1">
                                            {exp.achievements.map((ach, idx) => (
                                                <li key={idx}>{ach}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {exp.skills && exp.skills.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-3">
                                        {exp.skills.map((skill, idx) => (
                                            <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
